/**
 * Fullscreen modal (portal into document.body) showing a grid of up to maxImages
 * (default 6) image slots: filled slots render the image with a remove button,
 * empty slots are file-upload targets. Works on an ImageMap (url/objectURL ->
 * File | string | null) mutated via an immer-style setImageMap callback; in
 * "edit" mode, removing an already-uploaded (non-File) image also records its
 * key via setImagesToDelete for server-side deletion. Escape closes the modal.
 * Used by the bounty create/edit page (BountyCreateEdit).
 */
import React, { useEffect } from "react";
import { Plus, X } from "lucide-react";
import { BountyCreateEditMode } from "@/pages/bounty/BountyCreateEdit";
import { SubmissionType } from "@/types/submission";
import { createPortal } from "react-dom";

export type ImageMap = Map<string, File | string | null>;
interface ImagePickerProps {
    imageMap: ImageMap;
    maxImages?: number;
    setImageMap: (callback: (draft: ImageMap) => void) => void;
    setImagesToDelete?: (callback: (draft: string[]) => void) => void;
    onClose: () => void;
    type: BountyCreateEditMode | SubmissionType;
}

const ImagePicker: React.FC<ImagePickerProps> = ({
    imageMap,
    maxImages = 6,
    setImageMap,
    onClose,
    setImagesToDelete,
    type,
}) => {
    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files && imageMap.size < maxImages) {
            setImageMap((draft) => {
                Array.from(files).forEach((file) => {
                    draft.set(URL.createObjectURL(file), file);
                });
            });
        }
    };

    const handleRemoveImage = (key: string) => {
        const value = imageMap.get(key);
        const isFile = value instanceof File;
        setImageMap((draft) => {
            draft.delete(key);
        });
        if (!isFile && type === "edit" && setImagesToDelete) {
            setImagesToDelete((draft) => {
                draft.push(key);
            });
        }
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                onClose();
            }
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [onClose]);

    const renderImageGrid = () => {
        const imagesArray = Array.from(imageMap.entries());
        const imageSlots = [];

        // Create all image slots (existing + empty)
        for (let i = 0; i < maxImages; i++) {
            const imageKey = imagesArray[i] ? imagesArray[i][0] : null;
            imageSlots.push(imageKey);
        }

        return (
            <div className="relative">
                <div
                    className="overflow-y-auto rounded-lg bg-black bg-opacity-50 p-3 md:p-8 lg:p-16 w-[90vw] max-h-[85vh] md:max-h-[90vh] md:w-auto"
                    style={{
                        zIndex: 100000,
                        WebkitOverflowScrolling: "touch",
                    }}
                >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 place-items-center py-4">
                        {imageSlots.map((image, index) => (
                            <div key={index} className="relative w-64 h-64">
                                {image ? (
                                    <>
                                        <img
                                            src={image}
                                            alt={`Upload ${index + 1}`}
                                            className="w-full h-full object-cover rounded border-2 border-gray-300"
                                        />
                                        <button
                                            className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 hover:bg-black/80"
                                            onClick={() => handleRemoveImage(image)}
                                        >
                                            <X size={16} />
                                        </button>
                                    </>
                                ) : (
                                    <label className="w-full h-full border-2 border-solid border-gray-300 rounded flex items-center justify-center cursor-pointer hover:border-gray-400">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={handleImageUpload}
                                        />
                                        <Plus
                                            size={24}
                                            className="text-white cursor-pointer hover:text-gray-600"
                                        />
                                    </label>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
                <div className="absolute top-2 right-4 z-10">
                    <X className="w-8 h-8 text-white cursor-pointer" onClick={onClose} />
                </div>
            </div>
        );
    };

    const modal = (
        <div
            className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50"
            style={{ zIndex: 99999 }}
        >
            {renderImageGrid()}
        </div>
    );

    return createPortal(modal, document.body);
};

export default ImagePicker;
